import * as React from 'react'
import { useTablePaginationStyles } from '../../component/datatable/hook/useStyles'
import { useDispatch, useSelector } from '../../store'
import { Comment } from '../../store/support/reducer'
import { EventSourcePolyfill, MessageEvent } from 'event-source-polyfill'
import { showSnackbar } from '../../store/common/actionCreator'
import { sendChatMessage } from '../../store/support/actionCreator'
import {
  Avatar,
  Box,
  Button,
  Paper,
  Stack,
  TextareaAutosize,
  Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material'
import { t } from 'i18next'
import support_ from '../../sass/support.module.sass'
import {
  SupportAgent as SupportIcon,
} from '@mui/icons-material'
import { formatDate } from '../../function/date'
import { FilePreview } from '../../component/support/FilePreview'
import { SvgIcon } from '../../component/SvgIcon'
import { styled } from '@mui/material/styles'
import { refreshToken } from '../../store/auth/service'
import RefreshIcon from '@mui/icons-material/Refresh'

import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import { api } from '../../function/axios'

export const FILE_SIZE_LIMIT = 1024 * 1024 * 4
export const FILE_FORMATS = ['pdf', 'png', 'jpeg', 'jpg', 'gif', 'tiff']

let prevCommentsLength = 0

const Input = styled('input')({
  display: 'none',
})

interface Props {
  ticketId: number
}

export function Chat({ ticketId }: Props) {
  const paginationClasses = useTablePaginationStyles()
  const dispatch = useDispatch()

  const { avatar } = useSelector((state) => state.settings)

  const [connection, setConnection] = React.useState<any>(null)
  const [comments, setComments] = React.useState<Comment[]>([])
  const [rejectOpen, setRejectOpen] = React.useState(false)
  const [rejectId, setRejectId] = React.useState<number | null>(null)
  const [rejectText, setRejectText] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [input, setInput] = React.useState('')
  const [attach, setAttach] = React.useState<File>()

  // открыть модалку и подставить текущий текст
  const openReject = (id: number, initialText: string) => {
    setRejectId(id)
    setRejectText(initialText || '')
    setRejectOpen(true)
  }

  const closeReject = () => {
    setRejectOpen(false)
    setRejectId(null)
    setRejectText('')
  }
  
  const confirmReject = async () => {
    if (rejectId == null) return
    try {
      setIsSubmitting(true)

      const res = await api({
        method: 'POST',
        url: `/comment/reject/${rejectId}`,
        body: { 
          rejectedMessage : rejectText,
          },
      })
  
      const newSuggested = res.data?.suggestedAnswer ?? ''
      
      if (res.data?.message) {
        dispatch(showSnackbar('error',res.data?.message))
        return
      }

      setComments(prev =>
        prev.map(c =>
          c.id === rejectId ? { ...c, suggestedAnswer: newSuggested } : c,
        ),
      )
      
      closeReject()
    } catch (e) {
      console.error(e)
      dispatch(showSnackbar('error', 'Reject failed'))
    } finally {
      setIsSubmitting(false)
    }
  }
  
  console.log(comments)

  const typedInput = input.length

  const attachmentObjectUrl = React.useMemo(() => {
    if (!attach) return null

    const file = new Blob([attach], { type: attach.type })
    return {
      fileName: attach.name,
      url: URL.createObjectURL(file),
      isObjectUrl: true,
    }
  }, [attach])

  const handleSseConnect = React.useCallback(async () => {
    const token = await refreshToken()

    const eventSource = new EventSourcePolyfill(
      `${process.env.REACT_APP_API_BASE_URL}/sse/ticket/${ticketId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )

    eventSource.onmessage = function (event: MessageEvent) {
      const data: Comment[] = JSON.parse(event.data)

      if (prevCommentsLength !== data.length) setComments(data)

      prevCommentsLength = data.length
    }

    setConnection(eventSource)
  }, [ticketId])

  React.useEffect(() => {
    handleSseConnect()
  }, [handleSseConnect])

  React.useEffect(() => {
    return () => {
      connection?.close()
      prevCommentsLength = 0
    }
  }, [connection])

  function handleAttach(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return

    const file = e.target.files[0]
    const type = file.type.split('/')[1] || ''

    if (file.size > FILE_SIZE_LIMIT)
      dispatch(
        showSnackbar(
          'error',
          'Maximum file size: ' + FILE_SIZE_LIMIT / 1000 + 'KB',
        ),
      )
    else if (!FILE_FORMATS.includes(type))
      dispatch(
        showSnackbar(
          'error',
          `Only ${FILE_FORMATS.map((el) => ' ' + el)} formats are allowed`,
        ),
      )
    else setAttach(file)

    e.target.value = ''
  }

  function handleMessageSend() {
    if (input.trim() === '') {
      dispatch(showSnackbar('error', 'You cannot send empty message'))
      return
    }

    const formData = new FormData()

    formData.append('ticketId', String(ticketId) as string)
    formData.append('context', input)
    if (attach) formData.append('file', attach)

    dispatch(sendChatMessage(formData))
    setInput('')
    setAttach(undefined)
  }

  function handleMessageKeydown(e: React.KeyboardEvent<HTMLTextAreaElement>) {

    if (e.key === 'Enter' && e.shiftKey) {
      return
    }
  
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleMessageSend()
    }
  }

  function handleFileDelete() {
    setAttach(undefined)
  }

  async function handleGenerateAnswer(commentId: number) {
    try {
      const res = await api({
        method: 'PUT',
        url: `/comment/${commentId}`,
      })

      const newSuggested = res.data?.suggestedAnswer ?? ''
      
      if (res.data?.message) {
        dispatch(showSnackbar('error',res.data?.message))
        return
      }

      setComments(prev =>
        prev.map(c =>
          c.id === commentId ? { ...c, suggestedAnswer: newSuggested } : c,
        ),
      )
    } catch (e) {
      console.error(e)
    }
  }

  async function handleApproveAnswer(commentId: number, approvedMessage: string | null) {
    if (!approvedMessage) {
      dispatch(showSnackbar('error', 'Message is empty'))
      return
    }
  
    try {
      const formData = new FormData()
      formData.append('ticketId', String(ticketId))
      formData.append('context', approvedMessage)
      dispatch(sendChatMessage(formData))
      setAttach(undefined)
      const res = await api({
        method: 'POST',
        url: `/comment/approve/${commentId}`,
        body: { 
            approvedMessage : approvedMessage ,
          },
      })
  
      if (res.data?.message) {
        setComments(prev =>
          prev.map(c => (c.id === commentId ? { ...c, suggestedAnswer: null } : c)),
        )
  
        dispatch(showSnackbar('success', res.data.message))
      } else {
        dispatch(showSnackbar('error', 'Message is empty'))
      }
    } catch (e) {
      console.error(e)
      dispatch(showSnackbar('error', 'Approve failed'))
    }
  }

  return (
    <>
      <Typography variant="bold">{t('support.comments')}</Typography>
      <Box
        sx={{
          minHeight: 550,
          maxHeight: 550,
          width: "100%",
          overflowY: 'auto',
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          bgcolor: 'background.paper',
          mb: 2,
        }}
      >
        {comments.map((el, idx) => {
          const isSupport = el.user.roleId !== 1
          const name =
            el.user.firstname || el.user.lastname
              ? `${el.user.firstname || ''} ${el.user.lastname || ''}`.trim()
              : 'Support agent'

          return (
            <Stack
              key={idx}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{
                mb: 2,
                flexDirection: isSupport ? 'row' : 'row-reverse',
              }}
            >
              <Stack alignItems="center" spacing={0.5}>
                {el.user.avatar ? (
                  <Avatar src={el.user.avatar} sx={{ width: 36, height: 36 }} />
                ) : isSupport ? (
                  <Box className={support_.avatar__bg}>
                    <SupportIcon />
                  </Box>
                ) : (
                  <Avatar sx={{ width: 36, height: 36 }} />
                )}
                <Typography
                  color="text.secondary"
                  fontSize={10}
                  sx={{ mt: 0.5 }}
                >
                  {formatDate(el.createdTime, 'dd.MM.yyyy HH:mm')}
                </Typography>
              </Stack>

              <Paper
                elevation={1}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  maxWidth: '70%',
                  bgcolor: isSupport ? '#86b7d6' : '#3898d3',
                  color: isSupport ? 'text.primary' : 'common.white',
                  ...(isSupport
                    ? { ml: 3 } 
                    : { mr: 5 }),
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 12,
                    width: 0,
                    height: 0,
                    border: '6px solid transparent',
                    ...(isSupport
                      ? {
                        left: -12,
                        borderRightColor: '#86b7d6',
                      }
                      : {
                        right: -12,
                        borderLeftColor: '#3898d3',
                      }),
                  },
                }}
              >
                <Stack spacing={0.5}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{
                      color: isSupport ? 'text.primary' : 'common.white',
                    }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    fontSize={14}
                    sx={{
                      whiteSpace: 'pre-line',
                      color: isSupport ? 'text.primary' : 'common.white',
                    }}
                  >
                    {el.context}
                  </Typography>
                  {el.attachment && <FilePreview attachment={el.attachment} />}
                </Stack>
              </Paper>
              {(el.suggestedAnswer && !isSupport) && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    maxWidth: '70%',
                    bgcolor: '#d3d3d3',
                    color: 'text.primary',
                    mr: 5,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 12,
                      width: 0,
                      height: 0,
                      border: '6px solid transparent',
                      right: -12,
                      borderLeftColor: '#d3d3d3',

                    },
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{
                        color: 'text.primary',
                      }}
                    >
                      Suggested answer (AI)
                    </Typography>
                    <Typography
                      fontSize={14}
                      sx={{
                        whiteSpace: 'pre-line',
                        color: 'text.primary',
                      }}
                    >
                      {el.suggestedAnswer}
                    </Typography>
                    <Stack direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      justifyContent="flex-end"
                      sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        color="warning"
                        style={{
                          width: 20,
                          height: 20,
                          minWidth: 0,
                          borderRadius: '50%',
                          padding: 0,
                        }}
                        onClick={() => { handleGenerateAnswer(el.id) }}
                      >
                        <RefreshIcon sx={{ width: 12, height: 12 }} />
                      </Button>

                      <Button
                        variant="contained"
                        color="success"
                        sx={{
                          width: 20,
                          height: 20,
                          minWidth: 0,
                          borderRadius: '50%',
                          p: 0,
                        }}
                        onClick={() => { handleApproveAnswer(el.id, el.suggestedAnswer) }}
                      >
                        <ThumbUpIcon sx={{ width: 12, height: 12 }} />
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        sx={{
                          width: 20,
                          height: 20,
                          minWidth: 0,
                          borderRadius: '50%',
                          p: 0,
                        }}
                        onClick={() => { openReject(el.id, el.suggestedAnswer ?? '') }}
                      >
                        <ThumbDownIcon sx={{ width: 12, height: 12 }} />
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )
        })}
      </Box>
      <Dialog
        open={rejectOpen}
        onClose={closeReject}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            height: { xs: '45vh', md: '55vh' },
            maxHeight: 'none',
          },
        }}
      >
        <DialogTitle>Edit text before rejecting</DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 1, height: '50%' }}
        >
          <Box
            sx={{
              p: 2,
              height: '50%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={8}
              value={rejectText}
              onChange={(e) => setRejectText(e.target.value)}
              sx={{
                flex: 1, // занимает всю доступную высоту
                '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                '& textarea': { height: '100%', overflow: 'auto' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            mt: 'auto',
            pt: 2,
            pb: 3, px: 5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button onClick={closeReject} disabled={isSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReject}
            disabled={isSubmitting}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Stack direction="row" spacing={1} alignItems="flex-start"
        sx={{
          mb: 2,
        }}>
        <Avatar
          src={avatar || undefined}
          sizes="10"
          sx={{ width: 40, height: 40 }}
        />
        <Stack width="100%">
          <TextareaAutosize
            className="form__textarea"
            placeholder={t('field.hint.comments')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleMessageKeydown}
          />
          {attachmentObjectUrl && (
            <Stack direction="row" flexWrap="wrap" p={1} pb={0}>
              <FilePreview
                attachment={attachmentObjectUrl}
                onDelete={handleFileDelete}
              />
            </Stack>
          )}
        </Stack>
        <Button
          variant="contained"
          sx={{ 
            padding: 0, 
            minWidth: 40, 
            height: 40,
            borderRadius: '50%',
          }}
          onClick={handleMessageSend}
        >
          <SvgIcon name="send" size={{ width: 15, height: 15 }} />
        </Button>
        <label htmlFor="icon-button-file">
          <Input id="icon-button-file" type="file" onChange={handleAttach} />
          <Button
            variant="contained"
            sx={{ 
              padding: 0, 
              minWidth: 40, 
              height: 40,
              borderRadius: '50%',
            }}
            component="span"
          >
            <SvgIcon
              name="attach"
              size={{ width: 15, height: 15 }}
              color="white"
            />
          </Button>
        </label>
      </Stack>
    </>
  )
}
